import { useCallback, useEffect, useRef } from 'react'

import { usePostImpressionTracker } from './usePostImpressionTracker'

const VISIBILITY_THRESHOLD = 0.5
const DWELL_TIME_MS = 500

export const useTrackPostImpression = (postId: string) => {
  const { trackImpression, enabled } = usePostImpressionTracker()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [])

  const ref = useCallback(
    (element: Element | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }

      if (!enabled || !element) return

      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0]
          if (!entry) return

          if (entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD) {
            if (!timerRef.current) {
              timerRef.current = setTimeout(() => {
                trackImpression(postId)
                timerRef.current = null
                if (observerRef.current) {
                  observerRef.current.disconnect()
                  observerRef.current = null
                }
              }, DWELL_TIME_MS)
            }
          } else {
            if (timerRef.current) {
              clearTimeout(timerRef.current)
              timerRef.current = null
            }
          }
        },
        { threshold: VISIBILITY_THRESHOLD }
      )

      observerRef.current = observer
      observer.observe(element)
    },
    [postId, trackImpression, enabled]
  )

  return ref
}
